import React, { useState } from 'react';
import { StringHelpers, SystemText } from '@salesperformanceint/e4shared';
import config from '../../../../../../../config';
import { useInterval } from 'helpers/react/hooks';
import { ReactMediaRecorder } from '../mediaRecorder';
import './video.css';

export const VideoPreview = ({ stream }) => {
    const videoRef = React.useRef(null);

    React.useEffect(() => {
        if (videoRef.current && stream) {
            videoRef.current.srcObject = stream;
        }
    }, [stream]);
    if (!stream) {
        return null;
    }

    return (
        <div>
            <video ref={videoRef} width="100%" autoPlay controls={false} />
        </div>
    );
};

export const VideoCountdown = ({ recording, readyFont }) => {
    const [counter, setCounter] = React.useState(3);

    React.useEffect(() => {
        setCounter(3);
    }, [recording]);

    useInterval(() => {
        if (recording && counter > 0) {
            setCounter(counter - 0.5);
        }
    }, 500);

    return (
        recording && (
            <div className="video-countdown">
                <div
                    className="recording-icon"
                    style={{
                        display: counter % 1 === 0 || counter === 0 ? '' : 'none',
                    }}
                >
                    <i className={`fa fa-2x fa-circle-play text-danger`} />
                </div>
                {counter > 0 && (
                    <div
                        className="readying"
                        style={{
                            top: undefined,
                            left: '45%',
                            padding: 'auto',
                            fontSize: `${readyFont}px`,
                        }}
                    >
                        {Math.ceil(counter)}
                    </div>
                )}
            </div>
        )
    );
};

export const VideoTimer = ({ clock, recording, playing }) => {
    const [internalClock, setInternalClock] = React.useState(0);

    React.useEffect(() => {
        setInternalClock(clock);
    }, [clock]);

    useInterval(() => {
        if (recording || playing) {
            setInternalClock(internalClock + 1);
        }
    }, 1000);

    return <div className="video-time">{StringHelpers.displaySeconds(internalClock)}</div>;
};

export const Video = React.memo(
    ({ videoPosition, value, fileName, onChange }) => {
        const defaultReadyFont = 90;

        const [hasCreated, setHasCreated] = useState(false);
        const [recording, setRecording] = React.useState(false);
        const [playing, setPlaying] = React.useState(false);
        const [clock, setClock] = React.useState(0);

        const preview = React.useRef(null);
        const buttonContainer = React.useRef(null);

        React.useEffect(() => {
            return () => {
                const video = document.querySelector('video');
                if (video?.srcObject) {
                    for (const track of video.srcObject.getTracks()) {
                        track.stop();
                    }

                    video.srcObject = null;
                }
            };
        }, []);

        React.useEffect(() => {
            if (preview?.current && isFinite(videoPosition)) {
                preview.current.src = null;
                preview.current.srcObject = null;
                preview.current.src = `${config.storage.URL}${config.environment}/${value.url}`;
                preview.current.controls = true;
                preview.current.currentTime = videoPosition;
                preview.current.play();
            }
        }, [videoPosition, preview, value]);

        const onPlay = () => {
            setPlaying(true);
            preview.current.play();
        };

        const onPause = () => {
            preview.current.pause();
            setPlaying(false);
        };

        const onStartRecording = callback => {
            setClock(0);
            setPlaying(false);
            setRecording(true);
            setTimeout(() => callback(), 3000);
        };

        const onStopRecording = async blobUrl => {
            setHasCreated(true);
            setRecording(false);
            await fetch(blobUrl)
                .then(r => r.blob())
                .then(blobFile => new File([blobFile], `${fileName}.mp4`, { type: 'video/mp4' }))
                .then(file => onChange?.(file));
        };

        const streamingUrl = value && `${config.storage.URL}${config.environment}/${value.url}`;

        const constraints = {
            video: { width: 640, height: 480 },
            audio: { echoCancellation: { exact: true } },
        };

        return (
            <div>
                <div className="video-recorder mb10" style={{ width: '100%' }}>
                    <VideoCountdown recording={recording} readyFont={defaultReadyFont} />
                    <ReactMediaRecorder
                        video={constraints.video}
                        audio={constraints.audio}
                        screen={false}
                        onStop={onStopRecording}
                        render={recorderProps => {
                            const hasRecorderError = recorderProps.error !== 'NONE';
                            const aquiring = recorderProps.status === 'acquiring_media';
                            const mode =
                                !playing && (((!streamingUrl || !hasCreated) && !recorderProps.mediaBlobUrl) || recording)
                                    ? 'PREVIEW'
                                    : 'PLAY';
                            return (
                                <div>
                                    <div className="video-container">
                                        <div
                                            className="text-center"
                                            style={{
                                                paddingTop: '30px',
                                                width: '100%',
                                                height: undefined,
                                                display: recorderProps.error !== 'NONE' ? '' : 'none',
                                            }}
                                        >
                                            <h4>An error has occurred</h4>
                                            <br />
                                            {recorderProps.error}
                                            {recorderProps.error && (
                                                <div className="mt10 mb20">
                                                    {SystemText('General/Controls/Video/WebcanOn')}
                                                </div>
                                            )}
                                        </div>

                                        <div
                                            style={{
                                                display: recorderProps.error !== 'NONE' || mode !== 'PREVIEW' ? 'none' : '',
                                            }}
                                        >
                                            <VideoPreview stream={recorderProps.previewStream} />
                                        </div>
                                        <div
                                            style={{
                                                display: recorderProps.error !== 'NONE' || mode === 'PREVIEW' ? 'none' : '',
                                            }}
                                        >
                                            <video
                                                ref={preview}
                                                onPlay={() => setPlaying(true)}
                                                onPause={() => setPlaying(false)}
                                                onTimeUpdate={() => setClock(Math.floor(preview.current.currentTime))}
                                                width="100%"
                                                src={recorderProps.mediaBlobUrl || streamingUrl}
                                                playsInline
                                                controls
                                            />
                                        </div>
                                    </div>
                                    <div className="video-buttons" ref={buttonContainer}>
                                        {recording ? (
                                            <span>
                                                <button
                                                    data-testid="button-stop"
                                                    className="btn btn-primary mr5 btn-danger"
                                                    onClick={recorderProps.stopRecording}
                                                    title={SystemText('General/Controls/Video/Buttons/Stop')}
                                                >
                                                    <i className="fa fa-stop" />
                                                </button>
                                            </span>
                                        ) : (
                                            <button
                                                data-testid="button-record"
                                                className="btn btn-primary mr5 btn-danger"
                                                onClick={() => onStartRecording(() => recorderProps.startRecording())}
                                                title={SystemText('General/Controls/Video/Buttons/Record')}
                                                disabled={hasRecorderError || aquiring}
                                            >
                                                <i className="fa fa-circle-dot" />
                                            </button>
                                        )}
                                        <button
                                            data-testid="button-play-pause"
                                            className="btn btn-primary mr5"
                                            onClick={() => (playing ? onPause() : onPlay())}
                                            id="play"
                                            disabled={recording || (!recorderProps.mediaBlobUrl && !streamingUrl)}
                                            title={SystemText(
                                                `General/Controls/Video/Buttons/${playing ? 'Pause' : 'Play'}`
                                            )}
                                        >
                                            <i className={`fa fa-${playing ? 'pause' : 'play'}`} />
                                        </button>
                                    </div>
                                </div>
                            );
                        }}
                    />
                    <VideoTimer clock={clock} recording={recording} playing={playing} />
                </div>
            </div>
        );
    },
    (prevProps, nextProps) => true
);

export default Video;
